import React, { useEffect, useState } from 'react';
import {Button, Dialog} from '@betareader/design-system';
import {useSelector} from '../../reduxHooks';
import { toast } from 'react-toastify';
import { withTranslation } from 'react-i18next';
import { authUtil, uxAnalyticsUtil } from '../../utils';
import Avatar from '../../components/avatar/avatar';

interface Props {
  open: boolean;
  onClose: () => void;
  reader: any;
  onRatingUpdated?: () => void;
  t: (key: string, options?: any) => string;
}

const BookSpecificRating: React.FC<Props> = ({
  open,
  onClose,
  reader,
  onRatingUpdated,
  t,
}) => {
  const currentBook = useSelector(state => state.book.currentBook);

  const [isLoading, setIsLoading] = useState(false);
  const [rating, setRating] = useState<number>(
    reader?.authorRating?.rating || 0
  );
  const [text, setText] = useState<string>(reader?.authorRating?.text || '');
  const [existingRating, setExistingRating] = useState<any>(
    reader?.authorRating || null
  );

  useEffect(() => {
    if (open && reader) {
      setExistingRating(reader.authorRating || null);
      setRating(reader.authorRating?.rating || 0);
      setText(reader.authorRating?.text || '');
    }
  }, [open, reader]);

  const handleSubmit = async () => {
    if (!reader?.user || !currentBook || rating === 0) {
      toast.error(t('PleaseSelectRating'));
      return;
    }

    setIsLoading(true);
    try {
      const idToken = await authUtil.getFreshIdToken();

      uxAnalyticsUtil.trackEvent({
        category: 'UserReviews',
        action: 'Submitted book-specific review',
        value: rating,
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/users/${reader.user._id}/reviews`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'br-token': idToken,
          },
          body: JSON.stringify({
            rating,
            text,
            bookId: currentBook._id,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        toast.success(t('RatingSubmittedSuccessfully'));
        if (onRatingUpdated) {
          onRatingUpdated();
        }
        onClose();
      } else {
        throw new Error(result.msg || t('FailedToSubmitRating'));
      }
    } catch (error) {
      console.error('Failed to submit rating:', error);
      toast.error(error instanceof Error ? error.message : t('FailedToSubmitRating'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setText('');
    setExistingRating(null);
    onClose();
  };

  const ratingDescription = [
    'ClickToRateReader',
    'RatingPoor',
    'RatingFair',
    'RatingGood',
    'RatingVeryGood',
    'RatingExcellent',
  ][rating];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('RateReaderForBook', { title: currentBook?.title })}
      actions={<>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading || rating === 0}
          aria-busy={isLoading}
        >
          {existingRating ? t('UpdateRating') : t('SubmitRating')}
        </Button>
      </>}
    >
        {isLoading ? (
          <p role="status">{t('Loading')}</p>
        ) : (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <Avatar
                name={reader?.user?.displayName}
                src={reader?.user?.photoUrl}
                diameter={40}
              />
              <div style={{ marginLeft: '15px' }}>
                <h3 style={{ margin: 0 }}>
                  {reader?.user?.displayName}
                </h3>
                <p
                  style={{
                    margin: '5px 0 0 0',
                    color: '#666',
                    fontSize: '14px',
                  }}
                >
                  {reader?.completedBookAt
                    ? t('CompletedOnDate', {
                        date: new Date(
                          reader.completedBookAt
                        ).toLocaleDateString(),
                      })
                    : t('ReaderForThisBook')}
                </p>
              </div>
            </div>

            <hr />

            <div style={{display: 'grid', gap: 16}}>
              <div role="group" aria-label={t('YourRating')}>
                {[1, 2, 3, 4, 5].map(value => (
                  <button
                    key={value}
                    type="button"
                    aria-label={`${value} / 5`}
                    aria-pressed={rating === value}
                    onClick={() => setRating(value)}
                    style={{
                      border: 0,
                      background: 'transparent',
                      color: value <= rating ? '#b27300' : '#767676',
                      fontSize: 28,
                      cursor: 'pointer',
                    }}
                  >★</button>
                ))}
                <p
                  style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}
                >
                  {t(ratingDescription)}
                </p>
              </div>

              <label className="br-field">
                <span className="br-field__label">{t('CommentsOptional')}</span>
                <textarea
                  className="br-field__input"
                  placeholder={t('ShareExperiencePlaceholder')}
                  value={text}
                  onChange={event => setText(event.target.value)}
                  rows={3}
                />
              </label>
            </div>
          </div>
        )}
    </Dialog>
  );
};

export default withTranslation()(BookSpecificRating);
